import { useActionsContext } from '@unifyapps/carbon/no-code/context/ActionsProvider/context';
import { useGetComputeContext } from '@unifyapps/carbon/no-code/hooks/computeContext';
import { ActionType } from '@unifyapps/defs/types/action';
import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import { useCallback } from 'react';

type Params = {
  interfaceRecord?: InterfaceEntity;
  loggedInUserId?: string;
};

type Return = () => void;

export const useProfileClick = ({ interfaceRecord, loggedInUserId }: Params): Return => {
  const { getComputeContext } = useGetComputeContext();
  const { doAction } = useActionsContext();

  const onProfileClick = useCallback(() => {
    const userNavItem = interfaceRecord?.properties.navigation.secondary?.find(
      (navItem) => navItem.type === 'USER_MENU',
    );

    const { profilePage } = userNavItem ?? {};

    /**
     * If a page has been configured to be the profile page, navigate to that page.
     */
    if (profilePage?.pageId) {
      void doAction?.({
        action: {
          actionType: ActionType.NavigateToPage,
          payload: {
            pageId: profilePage.pageId,
            pageInputs: profilePage.pageInputs,
          },
        },
        actionContext: getComputeContext(),
      });

      return;
    }

    /**
     * If no page has been configured, navigate to the user overview page.
     */
    void doAction?.({
      action: {
        actionType: ActionType.Navigate,
        payload: {
          path: `/users/${loggedInUserId}/overview`,
        },
      },
      actionContext: getComputeContext(),
    });
  }, [
    doAction,
    getComputeContext,
    interfaceRecord?.properties.navigation.secondary,
    loggedInUserId,
  ]);

  return onProfileClick;
};
